import { useEffect } from "react";
import {
  Modal,
  Textarea,
  TextInput,
  Button,
  Stack,
  Group,
  Badge,
  LoadingOverlay,
  Select,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useProductTypesStore } from "./store";
import { notifications } from "@mantine/notifications";

// Dialog for adding/removing description/example overrides on API attributes
export default function AttributeOverrideDialog({
  opened,
  onClose,
  attribute,
  marketplaceId,
  onUpdated,
}) {
  const { updateAttribute, loading } = useProductTypesStore();
  const form = useForm({
    initialValues: {
      descriptionOverride: "",
      exampleOverride: "",
      validationOverride: "",
      validationOverrideMode: "Add",
    },
  });

  useEffect(() => {
    if (attribute) {
      form.setValues({
        descriptionOverride: attribute.descriptionOverride || "",
        exampleOverride: attribute.exampleOverride || "",
        validationOverride: attribute.validationOverride || "",
        validationOverrideMode: attribute.validationOverrideMode || "Add",
      });
    }
  }, [attribute, opened]);

  if (!attribute) return null;

  const handleSubmit = async (values) => {
    try {
      // Build full payload expected by backend write DTO
      const payload = {
        code: attribute.code,
        hierarchyCode: attribute.hierarchyCode,
        label: attribute.label,
        labelTranslations: attribute.labelTranslations || [],
        description: attribute.description, // underlying source description stays the same
        descriptionTranslations: attribute.descriptionTranslations || [],
        example: attribute.example,
        defaultValue: attribute.defaultValue,
        requirementLevel: attribute.requirementLevel,
        type: attribute.type,
        variant: attribute.variant,
        typeParameters: attribute.typeParameters || null,
        roles: attribute.roles || [],
        validations: attribute.validations || null,
        descriptionOverride:
          values.descriptionOverride.trim() === ""
            ? null
            : values.descriptionOverride.trim(),
        exampleOverride:
          values.exampleOverride.trim() === ""
            ? null
            : values.exampleOverride.trim(),
        validationOverride:
          values.validationOverride.trim() === ""
            ? null
            : values.validationOverride.trim(),
        validationOverrideMode: values.validationOverrideMode,
      };

      await updateAttribute(marketplaceId, attribute.id, payload);
      notifications.show({
        title: "Saved",
        message: "Overrides updated",
        color: "green",
      });
      onUpdated?.();
      onClose();
    } catch (e) {
      notifications.show({
        title: "Error",
        message: e.message || "Failed to save overrides",
        color: "red",
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Overrides: ${attribute.code}`}
      size="lg"
    >
      <LoadingOverlay visible={loading} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Group>
            <Badge color="blue" variant="light">
              API Attribute
            </Badge>
            {(attribute.descriptionOverride ||
              attribute.exampleOverride ||
              attribute.validationOverride) && (
              <Badge color="grape" variant="light">
                Has Existing Overrides
              </Badge>
            )}
          </Group>
          <Textarea
            label="Description Override"
            description="Leave empty to use the original API description"
            minRows={3}
            {...form.getInputProps("descriptionOverride")}
          />
          <TextInput
            label="Example Override"
            description="Leave empty to use the original example"
            {...form.getInputProps("exampleOverride")}
          />
          <Textarea
            label="Validation Override"
            description="Leave empty to use the original validations. Format: TYPE|PARAM,TYPE|PARAM (e.g., MIN_LENGTH|5,PATTERN|^[A-Za-z0-9]+$)"
            placeholder="MIN_LENGTH|5,PATTERN|^[A-Za-z0-9]+$"
            minRows={2}
            {...form.getInputProps("validationOverride")}
          />
          <Select
            label="Validation Override Mode"
            description="Choose whether to add to or replace existing validations"
            data={[
              { value: "Add", label: "Add (extend existing validations)" },
              {
                value: "Override",
                label: "Override (replace all validations)",
              },
            ]}
            {...form.getInputProps("validationOverrideMode")}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
